import { Box3, Group, LoadingManager, Mesh, Object3D, Scene, Vector3 } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { MaterialManager } from '../materials/MaterialManager'
import { disposeObject3D } from '../utils/disposeObject3D'
import type { SceneMetrics } from './BedroomScene'

export class KitchenScene {
  private readonly scene: Scene
  private readonly loader: GLTFLoader
  private readonly materialManager: MaterialManager
  private root: Object3D | null = null
  private metrics: SceneMetrics | null = null

  public constructor(
    scene: Scene,
    loadingManager: LoadingManager,
    materialManager: MaterialManager,
  ) {
    this.scene = scene
    this.loader = new GLTFLoader(loadingManager)
    this.materialManager = materialManager
  }

  /** Load and prepare the scene without adding it to the live render tree. */
  public async prepare(modelUrl: string): Promise<SceneMetrics> {
    if (this.metrics) return this.metrics

    const [, gltf] = await Promise.all([
      this.materialManager.load(),
      this.loader.loadAsync(modelUrl),
    ])

    const root = gltf.scene
    try {
      root.name = 'KitchenEnvironment'
      this.applyDefaultVisibility(root)
      const materialSummary = this.materialManager.apply(root)

      let objects = 0
      let meshes = 0
      root.traverse((object) => {
        objects += 1
        if (!(object instanceof Mesh)) return
        meshes += 1
        object.frustumCulled = true
        if (!object.geometry.boundingSphere) object.geometry.computeBoundingSphere()
      })

      root.updateMatrixWorld(true)
      const size = new Box3().setFromObject(root).getSize(new Vector3())
      this.root = root
      this.metrics = { objects, meshes, size, materialSummary }
      return this.metrics
    } catch (error) {
      const disposed = disposeObject3D(root)
      this.materialManager.forgetDisposedMaterials(disposed.materials)
      throw error
    }
  }

  public show(): void {
    if (!this.root) throw new Error('KitchenScene.prepare() must finish before show().')
    if (this.root.parent !== this.scene) this.scene.add(this.root)
    this.root.updateMatrixWorld(true)
  }

  private applyDefaultVisibility(root: Object3D): void {
    root.traverse((object) => {
      const defaultVisibility = object.userData.web_default_visible
      if (
        defaultVisibility === false
        || defaultVisibility === 0
        || defaultVisibility === 'false'
        || defaultVisibility === 'False'
      ) {
        object.visible = false
      }
    })
  }

  public setGroupVisible(group: string, visible: boolean): number {
    if (!this.root) return 0
    const normalizedGroupName = group.startsWith('GROUP_') ? group : `GROUP_${group}`
    let matches = 0
    this.root.traverse((object) => {
      if (
        object.userData.web_group !== group
        && object.name !== group
        && object.name !== normalizedGroupName
      ) return
      object.visible = visible
      matches += 1
    })
    return matches
  }

  /**
   * Permanently remove terminal-state props and release only resources that
   * are not shared by anything left in the kitchen.
   */
  public releaseGroups(
    groups: readonly string[],
    nodeNames: readonly string[] = [],
  ): { nodes: number; meshes: number; geometries: number; materials: number; textures: number } {
    if (!this.root) return { nodes: 0, meshes: 0, geometries: 0, materials: 0, textures: 0 }

    const groupNames = new Set(groups)
    const normalizedGroupNames = new Set(groups.map((group) => (
      group.startsWith('GROUP_') ? group : `GROUP_${group}`
    )))
    const explicitNodeNames = new Set(nodeNames)
    const matches: Object3D[] = []

    this.root.traverse((object) => {
      const authoredGroup = object.userData.web_group
      if (
        (typeof authoredGroup === 'string' && groupNames.has(authoredGroup))
        || groupNames.has(object.name)
        || normalizedGroupNames.has(object.name)
        || explicitNodeNames.has(object.name)
      ) matches.push(object)
    })

    const matchSet = new Set(matches)
    const releaseRoots = matches.filter((object) => {
      let parent = object.parent
      while (parent) {
        if (matchSet.has(parent)) return false
        parent = parent.parent
      }
      return true
    })
    if (releaseRoots.length === 0) {
      return { nodes: 0, meshes: 0, geometries: 0, materials: 0, textures: 0 }
    }

    const releasedRoot = new Group()
    let nodes = 0
    let meshes = 0
    for (const object of releaseRoots) {
      object.removeFromParent()
      releasedRoot.add(object)
      object.traverse((entry) => {
        nodes += 1
        if (entry instanceof Mesh) meshes += 1
      })
    }

    const disposed = disposeObject3D(releasedRoot, this.root)
    this.materialManager.forgetDisposedMaterials(disposed.materials)
    releasedRoot.clear()
    this.root.updateMatrixWorld(true)

    return {
      nodes,
      meshes,
      geometries: disposed.geometries.size,
      materials: disposed.materials.size,
      textures: disposed.textures.size,
    }
  }

  /** Apply state-specific transforms embedded by the Blender web exporter. */
  public applyAuthoredTransforms(state: 'packed'): number {
    if (!this.root) return 0
    const positionKey = `web_${state}_position`
    const quaternionKey = `web_${state}_quaternion`
    const scaleKey = `web_${state}_scale`
    let matches = 0

    this.root.traverse((object) => {
      const position = object.userData[positionKey]
      const quaternion = object.userData[quaternionKey]
      const scale = object.userData[scaleKey]
      if (
        !this.isNumberTuple(position, 3)
        || !this.isNumberTuple(quaternion, 4)
        || !this.isNumberTuple(scale, 3)
      ) return

      object.position.set(position[0], position[1], position[2])
      object.quaternion.set(quaternion[0], quaternion[1], quaternion[2], quaternion[3])
      object.scale.set(scale[0], scale[1], scale[2])
      object.updateMatrix()
      matches += 1
    })
    if (matches > 0) this.root.updateMatrixWorld(true)
    return matches
  }

  private isNumberTuple(value: unknown, length: number): value is number[] {
    return Array.isArray(value)
      && value.length === length
      && value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
  }

  public listGroups(): string[] {
    if (!this.root) return []
    const groups = new Set<string>()
    this.root.traverse((object) => {
      const group = object.userData.web_group
      if (typeof group === 'string' && group) groups.add(group)
    })
    return [...groups].sort()
  }

  public dispose(): void {
    if (!this.root) return
    this.scene.remove(this.root)
    const disposed = disposeObject3D(this.root)
    this.materialManager.forgetDisposedMaterials(disposed.materials)
    this.root = null
    this.metrics = null
  }
}
